import moment from 'moment';
import {
  AUTO_RESPONSE_MIN_UNATTENDED_RATING,
  AUTO_RESPONSE_TRIGGER_RATING,
  GOOGLE_POST_STALE_DAYS,
  POST_DESCRIPTION_MAX_LENGTH,
  POST_TITLE_MAX_LENGTH,
} from '../constants';
import { createMockPdfBlob, MockSeoReportData, RgbColor } from '../utils/mockPdf';
import type { DatapointsQuery, ReplyTemplateBody, ServiceAreaBody } from '.';
import {
  KeywordRankingsPayload,
  StoreServiceAreaPayload,
  UberallAiPostBody,
  UberallAiPostPayload,
  UberallAiReplyPayload,
  UberallAiSettingsPayload,
  UberallAnalyticsPayload,
  UberallAutoResponseRuleBody,
  UberallAutoResponseRuleListPayload,
  UberallAutoResponseRulePayload,
  UberallAutoResponseTrigger,
  UberallCompletenessPayload,
  UberallDataPointPayload,
  UberallDataPointsPagePayload,
  UberallKeywordsPayload,
  UberallListingPayload,
  UberallListingsSummaryPayload,
  UberallLocationPayload,
  UberallLocationProfilePayload,
  UberallMetricPayload,
  UberallMetricSeriesPayload,
  UberallOpeningHoursPayload,
  UberallPhotoPayload,
  UberallPhotoSlot,
  UberallPhotosPayload,
  UberallPostDirectoriesPayload,
  UberallReplyTemplatePayload,
  UberallScorePayload,
  UberallSeoReportSentPayload,
  UberallSocialConnection,
  UberallSocialConnectionsPayload,
  UberallSocialPlatform,
  UberallSocialPostBody,
  UberallSocialPostListPayload,
  UberallSocialPostPayload,
  UberallStoreLinkPayload,
} from './contract';

const LOCATION_ID = 5626345;
const PHOTO_TARGET = 10;

const KEYWORDS_MAX = 25;

const iso = (daysAgo: number) => moment().subtract(daysAgo, 'days').toISOString();
const isoAhead = (daysAhead: number) => moment().add(daysAhead, 'days').toISOString();

export const mocked = <T>(build: () => T, delayMs = 220): Promise<T> =>
  new Promise((resolve, reject) => {
    setTimeout(() => {
      try {
        resolve(build());
      } catch (error: unknown) {
        reject(error);
      }
    }, delayMs);
  });

const emptyMetric = (): UberallMetricPayload => ({ value: null, previous: null, delta: null, deltaPercent: null });

const metric = (value: number, previous: number): UberallMetricPayload => ({
  value,
  previous,
  delta: value - previous,
  deltaPercent: previous ? Math.round(((value - previous) / previous) * 1000) / 10 : null,
});

const openingHours = (): UberallOpeningHoursPayload[] => [
  { dayOfWeek: 1, from1: null, to1: null, from2: null, to2: null, closed: true },
  { dayOfWeek: 2, from1: '11:30', to1: '14:30', from2: '18:30', to2: '22:30', closed: false },
  { dayOfWeek: 3, from1: '11:30', to1: '14:30', from2: '18:30', to2: '22:30', closed: false },
  { dayOfWeek: 4, from1: '11:30', to1: '14:30', from2: '18:30', to2: '22:30', closed: false },
  { dayOfWeek: 5, from1: '11:30', to1: '14:30', from2: '18:30', to2: '23:00', closed: false },
  { dayOfWeek: 6, from1: '11:30', to1: '23:00', from2: null, to2: null, closed: false },
  { dayOfWeek: 7, from1: '18:30', to1: '22:00', from2: null, to2: null, closed: false },
];

const buildLocation = (): UberallLocationPayload => ({
  id: LOCATION_ID,
  identifier: 'LB_PLANB',
  name: 'Plan B',
  status: 'ACTIVE',
  profileCompleteness: 87,
  streetAndNumber: '14 rue du Temple',
  addressExtra: null,
  zip: '89000',
  city: 'Auxerre',
  province: 'Bourgogne-Franche-Comté',
  country: 'FR',
  lat: 47.7982,
  lng: 3.5673,
  phone: '+33 3 86 00 00 00',
  cellphone: null,
  email: null,
  website: 'https://planb-auxerre.fr',
  descriptionShort: 'Bar à cocktails et petite restauration au centre d’Auxerre.',
  descriptionLong:
    'Plan B, bar à cocktails au cœur d’Auxerre. Cocktails maison, bières artisanales locales et ' +
    'planches à partager. Terrasse ouverte d’avril à octobre, concerts le vendredi soir, ' +
    'privatisation possible pour vos événements.',
  imprint: null,
  openingHours: openingHours(),
  specialOpeningHours: [
    {
      date: moment().add(30, 'days').format('YYYY-MM-DD'),
      from1: null,
      to1: null,
      from2: null,
      to2: null,
      closed: true,
    },
  ],
  openingHoursNotes: 'Fermé les jours fériés.',
  serviceAreas: [{ id: 1699, name: 'Auxerre, France', placeId: 'ChIJnxHiBh9u5kcRYJSMaMOCCwQ' }],
  keywords: [],
  categories: [{ id: 342, name: 'Bar à cocktails' }],
  attributes: [],
  paymentOptions: [{ id: 1, name: 'Carte bancaire' }],
  services: [],
  socialProfiles: [{ type: 'FACEBOOK', url: 'https://facebook.com/planbauxerre' }],
  photos: [],
  brands: [],
  videos: [],
});

const listing = (
  id: number,
  type: string,
  typeName: string,
  status: UberallListingPayload['status'],
  action: UberallListingPayload['action'],
  daysAgo: number | null,
): UberallListingPayload => ({
  id,
  type,
  typeName,
  category: 'DIRECTORY',
  status,
  listingUrl: status === 'SYNCED' ? `https://example.com/${type.toLowerCase()}` : null,
  action,
  mandatoryFieldsMissing: action === 'ADD_MANDATORY_FIELDS' ? ['descriptionShort'] : [],
  expectedSyncDuration: status === 'IN_PROGRESS' ? 48 : null,
  lastUpdateAttempt: daysAgo === null ? null : iso(daysAgo),
});

const buildListings = (): UberallListingPayload[] => [
  listing(1, 'GOOGLE', 'Google', 'SYNCED', null, 1),
  listing(2, 'FACEBOOK', 'Facebook', 'SYNCED', null, 2),
  listing(3, 'APPLE', 'Apple Plans', 'SYNCED', null, 3),
  listing(4, 'BING', 'Bing', 'SYNCED', null, 4),
  listing(5, 'WAZE', 'Waze', 'SYNCED', null, 5),
  listing(6, 'HERE', 'HERE', 'SYNCED', null, 6),
  listing(7, 'TOMTOM', 'TomTom', 'SYNCED', null, 7),
  listing(8, 'MAPPY', 'Mappy', 'LINKED', null, 8),
  listing(9, 'VIAMICHELIN', 'ViaMichelin', 'LINKED', null, 9),
  listing(10, 'NEXTDOOR', 'Nextdoor', 'LINKED', null, 10),
  listing(11, 'TRIPADVISOR', 'Tripadvisor', 'SUBMITTED', null, 2),
  listing(12, 'FOURSQUARE', 'Foursquare', 'SUBMITTED', null, 3),
  listing(13, 'INSTAGRAM', 'Instagram', 'SUBMITTED', null, 1),
  listing(14, 'PAGESJAUNES', 'Pages Jaunes', 'ACTION_REQUIRED', 'CLAIM', 9),
  listing(15, 'YELP', 'Yelp', 'ACTION_REQUIRED', 'VERIFY', 12),
  listing(16, 'CYLEX', 'Cylex', 'UPDATING', null, 0),
];

const photo = (id: number, type: string, seed: string): UberallPhotoPayload => ({
  id,
  type,
  url: `https://picsum.photos/seed/${seed}/480/360`,
  thumbnailUrl: `https://picsum.photos/seed/${seed}/160/120`,
});

const review = (
  id: number,
  directoryType: string,
  rating: number,
  text: string,
  authorName: string,
  daysAgo: number,
  replyText: string | null,
): UberallDataPointPayload => ({
  id,
  locationId: LOCATION_ID,
  type: 'REVIEW',
  directoryType,
  status: 'PUBLISHED',
  rating,
  text,
  authorName,
  createdAt: iso(daysAgo),
  foundAt: iso(daysAgo),
  supportsReply: true,
  replied: replyText !== null,
  replyMaxLength: 4000,
  approvalNeeded: false,
  hasRepliesInApprovalNeeded: false,
  replyText,
  repliedAt: replyText === null ? null : iso(Math.max(0, daysAgo - 1)),
});

const buildDatapoints = (): UberallDataPointPayload[] => [
  review(
    9001,
    'GOOGLE',
    5,
    'Cocktails excellents et accueil au top. La terrasse est parfaite l’été.',
    'Camille R.',
    1,
    null,
  ),
  review(9002, 'GOOGLE', 2, 'Une heure d’attente pour deux verres un vendredi soir. Dommage.', 'Julien M.', 2, null),
  review(9003, 'FACEBOOK', 4, 'Très bonne ambiance, un peu bruyant les soirs de concert.', 'Sophie L.', 4, null),
  review(
    9004,
    'GOOGLE',
    1,
    'Addition qui ne correspondait pas à la carte. Personne pour expliquer.',
    'Anonyme',
    5,
    null,
  ),
  review(
    9005,
    'GOOGLE',
    5,
    'Le meilleur bar d’Auxerre, les planches sont généreuses.',
    'Marc D.',
    7,
    'Merci beaucoup Marc, à très bientôt !',
  ),
  review(
    9006,
    'TRIPADVISOR',
    3,
    'Correct, mais la carte des cocktails a peu changé depuis un an.',
    'Hélène P.',
    9,
    null,
  ),
  review(
    9007,
    'GOOGLE',
    5,
    'Soirée privatisée pour un anniversaire, tout était parfait.',
    'Nadia B.',
    12,
    'Merci Nadia, ravis que la soirée vous ait plu !',
  ),
  review(9008, 'GOOGLE', 4, 'Bon rapport qualité-prix, service souriant.', 'Thomas G.', 15, null),
  review(9009, 'FACEBOOK', 5, 'Équipe adorable, cocktails créatifs.', 'Laurent V.', 21, 'Merci Laurent !'),
  review(9010, 'GOOGLE', 2, 'Terrasse pas nettoyée en arrivant à 18h.', 'Inès K.', 26, null),
];

const buildPosts = (): UberallSocialPostPayload[] => [
  {
    id: 7001,
    type: 'POST',
    status: 'PUBLISHED',
    title: 'Nouvelle carte d’été',
    description: 'Six nouveaux cocktails à la carte, dont trois sans alcool. À découvrir en terrasse.',
    imageUrl: 'https://picsum.photos/seed/post-carte/960/540',
    url: 'https://planb-auxerre.fr/carte',
    publicationDate: iso(GOOGLE_POST_STALE_DAYS + 5),
    dateStart: null,
    dateEnd: null,
    directories: ['GOOGLE', 'FACEBOOK'],
    statusPerDirectory: { GOOGLE: 'PUBLISHED', OTHER_DIRECTORIES: 'PUBLISHED' },
    publicationsCount: 2,
    isGoogle: true,
  },
  {
    id: 7002,
    type: 'OFFER',
    status: 'PUBLISHED',
    title: 'Happy hour -30%',
    description: 'Du mardi au jeudi de 18h à 20h, 30% sur toute la carte des cocktails.',
    imageUrl: 'https://picsum.photos/seed/post-happyhour/960/540',
    url: null,
    publicationDate: iso(GOOGLE_POST_STALE_DAYS + 18),
    dateStart: iso(GOOGLE_POST_STALE_DAYS + 18),
    dateEnd: iso(GOOGLE_POST_STALE_DAYS + 11),
    directories: ['GOOGLE'],
    statusPerDirectory: { GOOGLE: 'PUBLISHED' },
    publicationsCount: 1,
    isGoogle: true,
  },
  {
    id: 7003,
    type: 'EVENT',
    status: 'SCHEDULED',
    title: 'Concert jazz trio',
    description: 'Trio jazz en live vendredi soir à partir de 21h. Entrée libre.',
    imageUrl: null,
    url: 'https://planb-auxerre.fr/concerts',
    publicationDate: isoAhead(6),
    dateStart: isoAhead(13),
    dateEnd: isoAhead(13),
    directories: ['GOOGLE', 'FACEBOOK', 'INSTAGRAM'],
    statusPerDirectory: {},
    publicationsCount: 0,
    isGoogle: true,
  },
];

const buildTemplates = (): UberallReplyTemplatePayload[] => [
  {
    id: 't-1',
    emoji: '⭐',
    label: 'Merci 5 étoiles',
    text: 'Merci beaucoup {{prénom}} pour ce retour ! Toute l’équipe est ravie. À très bientôt.',
    minRating: 5,
    maxRating: 5,
    theme: 'Remerciement',
    createdAt: iso(120),
    updatedAt: iso(30),
  },
  {
    id: 't-2',
    emoji: '🙏',
    label: 'Excuse attente',
    text: 'Bonjour {{prénom}}, désolés pour l’attente. Nous renforçons l’équipe les soirs de concert.',
    minRating: 2,
    maxRating: 3,
    theme: 'Attente',
    createdAt: iso(110),
    updatedAt: iso(45),
  },
];

const buildAutoResponseRules = (): UberallAutoResponseRulePayload[] => [
  {
    id: 'ar-1',
    name: 'Avis 5★ — remerciement',
    status: 'ACTIVE',
    triggers: ['FIVE_STAR'],
    responses: [
      'Merci beaucoup pour votre retour ! Toute l’équipe du Plan B est ravie de vous avoir accueilli.',
      'Un grand merci pour ces cinq étoiles — au plaisir de vous revoir très vite au comptoir !',
      'Merci pour ce message, il fait chaud au cœur. À très bientôt au Plan B !',
    ],
    locationCount: 1,
    canEdit: true,
    dateCreated: iso(64),
    dateUpdated: iso(12),
  },
  {
    id: 'ar-2',
    name: 'Avis 4★ — merci et invitation',
    status: 'INACTIVE',
    triggers: ['FOUR_STAR'],
    responses: [
      'Merci pour votre visite et pour ce retour ! Dites-nous ce qui vous a manqué pour la cinquième étoile.',
      'Merci beaucoup ! On garde votre remarque en tête pour la prochaine fois.',
      'Ravis que la soirée vous ait plu — au plaisir de vous accueillir à nouveau.',
    ],
    locationCount: 1,
    canEdit: true,
    dateCreated: iso(40),
    dateUpdated: iso(40),
  },
  // Authored directly in Uberall's own UI: read-only here, and outside our manual-validation floor.
  {
    id: 'ar-3',
    name: 'Avis 1-2★ — reprise de contact (groupe)',
    status: 'INACTIVE',
    triggers: ['ONE_STAR', 'TWO_STAR'],
    responses: [
      'Nous sommes navrés de lire cela. Écrivez-nous à contact@planb-auxerre.fr, nous voulons comprendre.',
      'Merci de nous avoir alertés. Contactez-nous directement, nous reprenons contact sous 48 h.',
      'Ce retour nous préoccupe. Appelez-nous au 03 86 00 00 00 pour que nous en parlions.',
    ],
    locationCount: 4,
    canEdit: false,
    dateCreated: iso(210),
    dateUpdated: iso(95),
  },
];

const REPORT_RECIPIENT = 'contact@planb-auxerre.fr';

const socialConnection = (
  platform: UberallSocialPlatform,
  status: UberallSocialConnection['status'],
  accountName: string | null = null,
): UberallSocialConnection => ({
  platform,
  status,
  connected: status === 'CONNECTED',
  canConnect: status === 'NOT_CONNECTED',
  canDisconnect: status === 'CONNECTED',
  accountName,
});

const buildSocialConnections = (isFacebookConnected: boolean): UberallSocialConnectionsPayload => ({
  facebook: socialConnection(
    'FACEBOOK',
    isFacebookConnected ? 'CONNECTED' : 'NOT_CONNECTED',
    isFacebookConnected ? 'Plan B Auxerre' : null,
  ),
  instagram: socialConnection('INSTAGRAM', isFacebookConnected ? 'NOT_CONNECTED' : 'NOT_NEEDED'),
});

interface MockState {
  location: UberallLocationPayload;
  listings: UberallListingPayload[];
  logo: UberallPhotoPayload | null;
  cover: UberallPhotoPayload | null;
  gallery: UberallPhotoPayload[];
  menu: UberallPhotoPayload[];
  keywords: string[];
  ai: UberallAiSettingsPayload;
  templates: UberallReplyTemplatePayload[];
  serviceAreas: StoreServiceAreaPayload[];
  autoResponseRules: UberallAutoResponseRulePayload[];
  datapoints: UberallDataPointPayload[];
  posts: UberallSocialPostPayload[];
  socialConnections: UberallSocialConnectionsPayload;
  lastReportGeneratedAt: string | null;
  lastReportSentAt: string | null;
  nextId: number;
}

const state: MockState = {
  location: buildLocation(),
  listings: buildListings(),
  logo: photo(101, 'LOGO', 'planb-logo'),
  cover: photo(102, 'COVER', 'planb-cover'),
  gallery: [photo(103, 'GALLERY', 'cocktail'), photo(104, 'GALLERY', 'terrasse'), photo(105, 'GALLERY', 'concert')],
  menu: [photo(106, 'MENU', 'carte-cocktails')],
  keywords: ['bar à cocktails auxerre', 'happy hour auxerre', 'concert bar auxerre', 'terrasse auxerre'],
  ai: {
    tone: 'warm_artisanal',
    language: 'auto',
    signature: 'L’équipe Plan B',
    customInstructions: 'Proposer la réservation en ligne quand le client mentionne l’attente.',
    autoReplyEnabled: true,
  },
  templates: buildTemplates(),
  serviceAreas: [],
  autoResponseRules: buildAutoResponseRules(),
  datapoints: buildDatapoints(),
  posts: buildPosts(),
  socialConnections: buildSocialConnections(false),
  lastReportGeneratedAt: null,
  lastReportSentAt: null,
  nextId: 50000,
};

const takeId = () => {
  state.nextId += 1;
  return state.nextId;
};

const summarise = (): UberallListingsSummaryPayload => {
  const count = (status: string) => state.listings.filter(item => item.status === status).length;
  return {
    total: state.listings.length,
    synced: count('SYNCED'),
    actionRequired: count('ACTION_REQUIRED'),
    inProgress: count('IN_PROGRESS'),
    notStarted: count('NOT_STARTED'),
    unknown: count('UNKNOWN'),
    listings: state.listings,
  };
};

const photosPayload = (): UberallPhotosPayload => ({
  logo: state.logo,
  cover: state.cover,
  gallery: state.gallery,
  menu: state.menu,
  galleryCount: state.gallery.length,
  menuCount: state.menu.length,
  total: state.gallery.length + state.menu.length + (state.logo ? 1 : 0) + (state.cover ? 1 : 0),
});

const keywordsPayload = (): UberallKeywordsPayload => ({
  keywords: state.keywords,
  used: state.keywords.length,
  limit: KEYWORDS_MAX,
  remaining: Math.max(0, KEYWORDS_MAX - state.keywords.length),
});

const completeness = (): UberallCompletenessPayload => {
  const levels: UberallCompletenessPayload['levels'] = [
    {
      level: 'REQUIRED',
      weight: 40,
      earned: 40,
      fields: [
        { field: 'Nom', weight: 10, filled: true },
        { field: 'Adresse', weight: 10, filled: true },
        { field: 'Téléphone', weight: 10, filled: true },
        { field: 'Horaires', weight: 10, filled: true },
      ],
    },
    {
      level: 'BASIC',
      weight: 25,
      earned: 20,
      fields: [
        { field: 'Site web', weight: 5, filled: true },
        { field: 'Catégorie principale', weight: 5, filled: true },
        { field: 'Description courte', weight: 5, filled: true },
        { field: 'Logo', weight: 5, filled: state.logo !== null },
        { field: 'Adresse e-mail', weight: 5, filled: state.location.email !== null },
      ],
    },
    {
      level: 'ADVANCED',
      weight: 25,
      earned: 20,
      fields: [
        { field: 'Description longue', weight: 5, filled: true },
        { field: 'Photo de couverture', weight: 5, filled: state.cover !== null },
        { field: 'Galerie photos', weight: 5, filled: state.gallery.length >= 3 },
        { field: 'Mots-clés', weight: 5, filled: state.keywords.length > 0 },
        { field: 'Services', weight: 5, filled: false },
      ],
    },
    {
      level: 'BONUS',
      weight: 10,
      earned: 5,
      fields: [
        { field: 'Réseaux sociaux', weight: 5, filled: true },
        { field: 'Vidéos', weight: 5, filled: false },
      ],
    },
  ];
  const computedTotal = levels.reduce((sum, level) => sum + level.earned, 0);
  return {
    profileCompleteness: state.location.profileCompleteness,
    computedTotal,
    remaining: Math.max(0, 100 - computedTotal),
    levels,
  };
};

const unansweredCount = () => state.datapoints.filter(item => !item.replied).length;

const unfilledFieldCount = () =>
  completeness().levels.reduce((sum, level) => sum + level.fields.filter(field => !field.filled).length, 0);

const daysSinceGooglePost = (): number | null => {
  const ages = state.posts
    .filter(post => post.isGoogle && post.publicationDate && moment(post.publicationDate).isSameOrBefore(moment()))
    .map(post => moment().diff(moment(post.publicationDate), 'days'));
  return ages.length > 0 ? Math.min(...ages) : null;
};

const score = (): UberallScorePayload => {
  const { actionRequired } = summarise();
  const photosNeeded = Math.max(0, PHOTO_TARGET - photosPayload().total);

  const tasks: UberallScorePayload['tasks'] = [
    {
      key: 'SCORE_TASK_FIX_CONNECTORS',
      component: 'CONNECTORS',
      impact: 'PRIORITY',
      pointsGain: 8,
      count: actionRequired || null,
      target: 'SETTINGS_CONNECTORS',
    },
    {
      key: 'SCORE_TASK_ANSWER_REVIEWS',
      component: 'REVIEWS',
      impact: 'PRIORITY',
      pointsGain: 6,
      count: unansweredCount() || null,
      target: 'REVIEWS_INBOX',
    },
    {
      key: 'SCORE_TASK_PUBLISH_POST',
      component: 'POSTS',
      impact: 'IMPORTANT',
      pointsGain: 5,
      count: daysSinceGooglePost(),
      target: 'POSTS_COMPOSER',
    },
    {
      key: 'SCORE_TASK_COMPLETE_PROFILE',
      component: 'COMPLETENESS',
      impact: 'IMPORTANT',
      pointsGain: 4,
      count: unfilledFieldCount() || null,
      target: 'SETTINGS_PROFILE',
    },
    {
      key: 'SCORE_TASK_ADD_PHOTOS',
      component: 'PHOTOS',
      impact: 'RECOMMENDED',
      pointsGain: 3,
      count: photosNeeded || null,
      target: 'SETTINGS_PHOTOS',
    },
  ];

  return {
    score: 78,
    maxScore: 100,
    profileCompleteness: state.location.profileCompleteness,
    components: [
      { component: 'COMPLETENESS', weight: 30, earned: 26 },
      { component: 'CONNECTORS', weight: 25, earned: 17 },
      { component: 'REVIEWS', weight: 20, earned: 15 },
      { component: 'PHOTOS', weight: 15, earned: 12 },
      { component: 'POSTS', weight: 10, earned: 8 },
    ],
    openTasks: tasks.length,
    priorityTasks: tasks.filter(task => task.impact === 'PRIORITY').length,
    tasks,
  };
};

/**
 * The per-bucket series, split four ways like the real payload: `visibility.impressions` is their
 * sum, so a chart that plots one of them disagrees with the tile.
 */
const impressionSeries = (startDate: string, endDate: string): UberallMetricSeriesPayload[] => {
  const spanDays = Math.max(1, moment(endDate).diff(moment(startDate), 'days'));
  const isMonthly = spanDays > 62;
  const unit = isMonthly ? 'months' : 'days';
  const buckets = isMonthly ? Math.max(1, Math.round(spanDays / 30)) : spanDays;
  const cursor = moment(endDate).subtract(buckets - 1, unit);

  const surfaces: [string, number][] = [
    ['BUSINESS_IMPRESSIONS_MOBILE_SEARCH', 0.45],
    ['BUSINESS_IMPRESSIONS_DESKTOP_SEARCH', 0.25],
    ['BUSINESS_IMPRESSIONS_MOBILE_MAPS', 0.2],
    ['BUSINESS_IMPRESSIONS_DESKTOP_MAPS', 0.1],
  ];

  return surfaces.map(([name, share]) => ({
    name,
    granularity: isMonthly ? 'MONTHLY' : 'DAILY',
    points: Array.from({ length: buckets }, (_, index) => ({
      period: cursor.clone().add(index, unit).format('YYYY-MM-DD'),
      // A rising line with a little noise, so the chart looks like data rather than a ramp.
      count: Math.round((isMonthly ? 14000 : 600) * share * (1 + index / buckets) * (1 + ((index % 3) - 1) / 20)),
    })),
  }));
};

const analytics = (startDate: string, endDate: string): UberallAnalyticsPayload => {
  const spanDays = Math.max(1, moment(endDate).diff(moment(startDate), 'days'));
  const scale = spanDays / 30;
  const at = (base: number) => Math.round(base * scale);
  const answered = state.datapoints.length - unansweredCount();

  return {
    period: { startDate, endDate },
    comparedTo: {
      startDate: moment(startDate).subtract(spanDays, 'days').format('YYYY-MM-DD'),
      endDate: startDate,
    },
    overview: {
      directSearches: emptyMetric(),
      discoverySearches: emptyMetric(),
      averageRating: metric(4.4, 4.2),
      actions: {
        calls: metric(at(184), at(163)),
        directions: metric(at(412), at(455)),
        websiteVisits: metric(at(268), at(221)),
        clickAndCollect: metric(at(96), at(74)),
      },
    },
    visibility: {
      averageLocalPosition: metric(4.2, 5.1),
      impressions: metric(at(7340), at(6480)),
      profileCompleteness: state.location.profileCompleteness,
      impressionsByDirectory: [],
    },
    reviews: {
      averageRating: metric(4.4, 4.2),
      total: metric(state.datapoints.length, 8),
      responseRate: metric(Math.round((answered / state.datapoints.length) * 100), 45),
      unansweredCount: unansweredCount(),
      ratingBreakdown: { '1': 0.1, '2': 0.2, '3': 0.1, '4': 0.2, '5': 0.4 },
      recurringThemes: [
        { theme: 'Qualité des cocktails', share: 0.42, previousShare: 0.38, averageRating: 4.8 },
        { theme: 'Accueil', share: 0.24, previousShare: 0.27, averageRating: 4.5 },
        { theme: 'Temps d’attente', share: 0.18, previousShare: 0.12, averageRating: 2.9 },
        { theme: 'Niveau sonore', share: 0.1, previousShare: 0.08, averageRating: 3.4 },
        { theme: 'Prix', share: 0.06, previousShare: 0.09, averageRating: 2.6 },
      ],
    },
    searchKeywords: [
      { keyword: 'bar ouvert tard auxerre', volume: at(880) },
      { keyword: 'où boire un verre auxerre', volume: at(640) },
      { keyword: 'bar auxerre centre ville', volume: at(410) },
      { keyword: 'cocktail auxerre pas cher', volume: at(290) },
      { keyword: 'bar musique live bourgogne', volume: at(210) },
      { keyword: 'plan b auxerre', volume: null },
    ],
    searchKeywordsTotal: 47,
    series: impressionSeries(startDate, endDate),
  };
};

// --- lb-api-v2: store-scoped ---

export const mockStoreLink = (): UberallStoreLinkPayload => ({
  locationId: LOCATION_ID,
  takeoverDate: iso(400),
  reportRecipientEmail: REPORT_RECIPIENT,
  lastReportGeneratedAt: state.lastReportGeneratedAt,
  lastReportSentAt: state.lastReportSentAt,
  ai: state.ai,
  replyTemplates: state.templates,
});

export const mockAiSettings = () => state.ai;

export const mockPatchAiSettings = (patch: Partial<UberallAiSettingsPayload>) => {
  state.ai = { ...state.ai, ...patch };
  return state.ai;
};

export const mockReplyTemplates = () => state.templates;

export const mockCreateReplyTemplate = (body: ReplyTemplateBody): UberallReplyTemplatePayload => {
  const created: UberallReplyTemplatePayload = {
    id: `t-${takeId()}`,
    emoji: body.emoji ?? null,
    label: body.label,
    text: body.text,
    minRating: body.minRating ?? null,
    maxRating: body.maxRating ?? null,
    theme: body.theme ?? null,
    createdAt: iso(0),
    updatedAt: iso(0),
  };
  state.templates = [...state.templates, created];
  return created;
};

export const mockUpdateReplyTemplate = (
  templateId: string,
  patch: Partial<ReplyTemplateBody>,
): UberallReplyTemplatePayload => {
  const existing = state.templates.find(template => template.id === templateId);
  if (!existing) throw new Error(`Unknown template ${templateId}`);
  const updated: UberallReplyTemplatePayload = { ...existing, ...patch, updatedAt: iso(0) };
  state.templates = state.templates.map(template => (template.id === templateId ? updated : template));
  return updated;
};

export const mockDeleteReplyTemplate = (templateId: string) => {
  state.templates = state.templates.filter(template => template.id !== templateId);
};

export const mockServiceAreas = () => state.serviceAreas;

export const mockCreateServiceArea = (body: ServiceAreaBody): StoreServiceAreaPayload => {
  const created: StoreServiceAreaPayload = {
    id: `sa-${takeId()}`,
    name: body.name,
    placeId: body.placeId ?? null,
    createdAt: iso(0),
    updatedAt: iso(0),
  };
  state.serviceAreas = [...state.serviceAreas, created];
  return created;
};

export const mockUpdateServiceArea = (areaId: string, patch: Partial<ServiceAreaBody>): StoreServiceAreaPayload => {
  const existing = state.serviceAreas.find(area => area.id === areaId);
  if (!existing) throw new Error(`Unknown service area ${areaId}`);
  const updated: StoreServiceAreaPayload = {
    ...existing,
    ...('name' in patch ? { name: patch.name as string } : {}),
    ...('placeId' in patch ? { placeId: patch.placeId ?? null } : {}),
    updatedAt: iso(0),
  };
  state.serviceAreas = state.serviceAreas.map(area => (area.id === areaId ? updated : area));
  return updated;
};

export const mockDeleteServiceArea = (areaId: string) => {
  state.serviceAreas = state.serviceAreas.filter(area => area.id !== areaId);
};

class MockUberallError extends Error {
  response: { status: number; data: { status: string; message: string } };

  constructor(httpStatus: number, code: string, message: string) {
    super(message);
    this.name = 'MockUberallError';
    this.response = { status: httpStatus, data: { status: code, message } };
  }
}

const autoResponseRules = (): UberallAutoResponseRuleListPayload => ({
  rules: state.autoResponseRules,
  count: state.autoResponseRules.length,
});

const refuseUnattendedLowRatings = (body: UberallAutoResponseRuleBody) => {
  if (body.status !== 'ACTIVE') return;
  const tooLow = body.triggers.filter(
    trigger => AUTO_RESPONSE_TRIGGER_RATING[trigger] < AUTO_RESPONSE_MIN_UNATTENDED_RATING,
  );
  if (tooLow.length > 0) {
    throw new MockUberallError(
      400,
      'UBERALL_MANUAL_VALIDATION_REQUIRED',
      `${tooLow.join(', ')} cannot answer unattended`,
    );
  }
};

const refuseTriggerConflict = (body: UberallAutoResponseRuleBody, ignoreRuleId: string | null) => {
  if (body.status !== 'ACTIVE') return;
  const clash = state.autoResponseRules.find(
    rule =>
      rule.id !== ignoreRuleId &&
      rule.status === 'ACTIVE' &&
      rule.triggers.some(trigger => body.triggers.includes(trigger as UberallAutoResponseTrigger)),
  );
  if (clash) {
    throw new MockUberallError(
      409,
      'UBERALL_AUTO_RESPONSE_CONFLICT',
      `This location already has an active rule for that star band: ${clash.name}`,
    );
  }
};

const requireEditableRule = (ruleId: string): UberallAutoResponseRulePayload => {
  const existing = state.autoResponseRules.find(rule => rule.id === ruleId);
  if (!existing) throw new MockUberallError(404, 'UBERALL_AUTO_RESPONSE_RULE_NOT_FOUND', 'Rule not found');
  return existing;
};

export const mockAutoResponseRules = (): UberallAutoResponseRuleListPayload => autoResponseRules();

export const mockCreateAutoResponseRule = (body: UberallAutoResponseRuleBody): UberallAutoResponseRuleListPayload => {
  refuseUnattendedLowRatings(body);
  refuseTriggerConflict(body, null);
  state.autoResponseRules = [
    ...state.autoResponseRules,
    {
      id: `ar-${takeId()}`,
      name: body.name,
      status: body.status,
      triggers: [...body.triggers],
      responses: [...body.responses],
      locationCount: 1,
      canEdit: true,
      dateCreated: iso(0),
      dateUpdated: iso(0),
    },
  ];
  return autoResponseRules();
};

export const mockUpdateAutoResponseRule = (
  ruleId: string,
  body: UberallAutoResponseRuleBody,
): UberallAutoResponseRuleListPayload => {
  const existing = requireEditableRule(ruleId);
  refuseUnattendedLowRatings(body);
  refuseTriggerConflict(body, ruleId);
  const updated: UberallAutoResponseRulePayload = {
    ...existing,
    name: body.name,
    status: body.status,
    triggers: [...body.triggers],
    responses: [...body.responses],
    dateUpdated: iso(0),
  };
  state.autoResponseRules = state.autoResponseRules.map(rule => (rule.id === ruleId ? updated : rule));
  return autoResponseRules();
};

export const mockDeleteAutoResponseRule = (ruleId: string): UberallAutoResponseRuleListPayload => {
  requireEditableRule(ruleId);
  state.autoResponseRules = state.autoResponseRules.filter(rule => rule.id !== ruleId);
  return autoResponseRules();
};

// --- lb-services-v2: location-scoped ---

export const mockLocationProfile = (): UberallLocationProfilePayload => ({
  location: { ...state.location, keywords: state.keywords },
  completeness: completeness(),
});

export const mockPatchLocation = (patch: Record<string, unknown>): UberallLocationProfilePayload => {
  state.location = { ...state.location, ...(patch as Partial<UberallLocationPayload>) };
  return mockLocationProfile();
};

export const mockListings = () => summarise();

export const mockSyncListings = () => {
  state.listings = state.listings.map(item =>
    item.status === 'ACTION_REQUIRED'
      ? { ...item, status: 'IN_PROGRESS', action: null, mandatoryFieldsMissing: [], lastUpdateAttempt: iso(0) }
      : item,
  );
  return summarise();
};

export const mockPhotos = () => photosPayload();

export const mockUploadPhoto = (slot: UberallPhotoSlot, file: File): UberallPhotosPayload => {
  const objectUrl = URL.createObjectURL(file);
  const created: UberallPhotoPayload = { id: takeId(), type: slot, url: objectUrl, thumbnailUrl: objectUrl };
  if (slot === 'LOGO') state.logo = created;
  else if (slot === 'COVER') state.cover = created;
  else if (slot === 'MENU') state.menu = [...state.menu, created];
  else state.gallery = [...state.gallery, created];
  return photosPayload();
};

export const mockDeletePhoto = (photoId: number): UberallPhotosPayload => {
  if (state.logo?.id === photoId) state.logo = null;
  if (state.cover?.id === photoId) state.cover = null;
  state.gallery = state.gallery.filter(item => item.id !== photoId);
  state.menu = state.menu.filter(item => item.id !== photoId);
  return photosPayload();
};

export const mockKeywords = () => keywordsPayload();

export const mockPutKeywords = (keywords: string[]) => {
  state.keywords = keywords.slice(0, KEYWORDS_MAX);
  return keywordsPayload();
};

export const mockAnalytics = (startDate: string, endDate: string) => analytics(startDate, endDate);

export const mockScore = () => score();

/**
 * The fake report's content — thematically fitting "Plan B" (a bar, per its own
 * planb-auxerre.fr recipient address) rather than generic placeholder text, so the
 * preview reads as a real report would. Every number here is invented for the mock: the
 * real backend never fabricates a figure, but this local-only preview's entire point is
 * showing the design with no backend at all, so there is nothing real to be honest about.
 */
const ORANGE_TEXT: RgbColor = [249, 115, 22];
const GREEN_TEXT: RgbColor = [34, 197, 94];
const BLUE_TEXT: RgbColor = [59, 130, 246];
const BLUE_BG: RgbColor = [219, 234, 254];
const GREEN_BG: RgbColor = [220, 252, 231];
const RED_TEXT: RgbColor = [220, 38, 38];
const RED_BG: RgbColor = [254, 226, 226];
const GOLD_TEXT: RgbColor = [180, 120, 8];
const GOLD_BG: RgbColor = [253, 230, 180];

const mockSeoReportData = (): MockSeoReportData => {
  const businessName = state.location.name ?? 'Fiche sans nom';
  const city = state.location.city ?? 'Auxerre';
  const address = [state.location.streetAndNumber, state.location.zip, city].filter(Boolean).join(', ');
  const lastMonth = moment().subtract(1, 'month');
  const periodLabel = `${moment().subtract(3, 'months').format('MMMM YYYY')} - ${lastMonth.format('MMMM YYYY')}`;

  const chip = (statusColor: RgbColor, statusBg: RgbColor) => ({ statusColor, statusBg });
  const gold = chip(GOLD_TEXT, GOLD_BG);
  const green = chip(GREEN_TEXT, GREEN_BG);
  const blue = chip(BLUE_TEXT, BLUE_BG);
  const red = chip(RED_TEXT, RED_BG);

  return {
    businessName,
    businessAddress: address || city,
    periodLabel,
    monthBadge: lastMonth.format('MMMM YYYY'),
    kpis: [
      { value: '58 %', label: 'LOCALITÉS EN #1', color: ORANGE_TEXT },
      { value: '83 %', label: 'LOCALITÉS DANS LE TOP 3', color: GREEN_TEXT },
      { value: '100 %', label: 'LOCALITÉS DANS LE TOP 10', color: BLUE_TEXT },
    ],
    insightHeadline: `Bonne dynamique locale a ${city}, avec un potentiel geographique a activer.`,
    insightBody:
      `Sur votre coeur d'activite a ${city}, la fiche domine : 58 % de vos mots-cles suivis ` +
      `sont #1 et 100 % apparaissent en 1ere page. Les appels progressent de 17,62 % sur un an, ` +
      `alors que les impressions Maps reculent. Le vrai gisement de croissance : 5 mots-cles sur 12 ` +
      `ne sont pas classes, presque tous lies aux communes voisines (Joigny, Migennes, Monéteau).`,
    figuresRows: [
      {
        label: 'Impressions Google Search',
        value: '2 820',
        meaning: "Affichages dans la recherche Google : -17,34 % vs l'an dernier",
      },
      {
        label: 'Impressions Google Maps',
        value: '4 220',
        meaning: "Affichages sur Google Maps : -30,13 % vs l'an dernier",
      },
      {
        label: 'Clics vers le site web',
        value: '493',
        meaning: "Visites de votre site depuis la fiche : -10,69 % vs l'an dernier",
      },
      { label: 'Appels', value: '227', meaning: "Clics sur le bouton d'appel : +17,62 % vs l'an dernier" },
      { label: "Demandes d'itinéraire", value: '767', meaning: "Clics sur « Itinéraire » : -9,87 % vs l'an dernier" },
      {
        label: 'Localités en #1',
        value: '58 %',
        meaning: 'Part des mots-clés classés positionnés en première position',
      },
      {
        label: 'Localités dans le Top 3',
        value: '83 %',
        meaning: 'Part des mots-clés classés dans les 3 premiers résultats',
      },
      {
        label: 'Localités dans le Top 10',
        value: '100 %',
        meaning: 'Part des mots-clés classés présents en 1re page Google',
      },
    ],
    keywordRows: [
      { keyword: `Bar ${city}`, position: '#1', status: '#1 Google', ...gold },
      { keyword: `Bar a cocktails ${city}`, position: '#1', status: '#1 Google', ...gold },
      { keyword: `${businessName} ${city}`, position: '#1', status: '#1 Google', ...gold },
      { keyword: `Bar branche ${city}`, position: '#3', status: 'Top 3', ...green },
      { keyword: `Meilleur bar ${city}`, position: '#6', status: 'Top 10', ...blue },
      { keyword: `Bar ${city} centre`, position: '#14', status: 'Hors Top 10', ...red },
      { keyword: `Bar Joigny`, position: '-', status: 'Non classé', ...red },
      { keyword: `Bar Migennes`, position: '-', status: 'Non classé', ...red },
      { keyword: `Bar Monéteau`, position: '-', status: 'Non classé', ...red },
    ],
    attentionText:
      '5 mots-clés sur 12 ne sont pas classés, tous liés aux communes voisines (Joigny, ' +
      'Migennes, Monéteau) - un potentiel de conquête géographique à activer.',
    actions: [
      {
        title: 'Conquérir les communes voisines',
        objective: `Faire apparaître "Bar Joigny", "Bar Migennes" et "Bar Monéteau" d'ici 2 mois`,
        bullets: [
          'Mentionner explicitement les communes desservies dans la description',
          'Publier des posts Google mentionnant la zone de chalandise',
          'Solliciter des avis de clients issus des communes voisines',
        ],
        impact: 'Élargir le rayon de captation au-delà du seul nom de la ville',
        color: BLUE_TEXT,
      },
      {
        title: 'Enrichir la fiche chaque semaine',
        objective: 'Maintenir un signal de fraîcheur constant pour Google',
        bullets: [
          'Publier une photo par semaine (ambiance, carte, événements)',
          'Publier un post Google par semaine (soirée, offre, nouveauté)',
        ],
        impact: "Une fiche active est mieux servie par Google qu'une fiche dormante",
        color: GREEN_TEXT,
      },
      {
        title: 'Convertir les positions #2-3 en #1',
        objective: 'Faire passer en #1 les mots-clés déjà en bonne position',
        bullets: [
          "Lancer une campagne d'avis clients pour renforcer l'autorité de la fiche",
          'Maintenir une cadence régulière de posts et de mises à jour',
        ],
        impact: 'Consolider la position dominante déjà acquise sur le coeur de zone',
        color: GOLD_TEXT,
      },
    ],
    footerNote: `LittleBill - Rapport genere en mode maquette - ${moment().format('DD/MM/YYYY')} - Apercu local, non envoye par email`,
  };
};

export const mockGenerateSeoReport = (): Blob => {
  state.lastReportGeneratedAt = iso(0);
  return createMockPdfBlob(mockSeoReportData());
};

const requireMockReport = (): string => {
  if (!state.lastReportGeneratedAt) {
    throw new MockUberallError(409, 'SEO_REPORT_NOT_GENERATED', 'No SEO report has been generated yet');
  }
  return state.lastReportGeneratedAt;
};

export const mockDownloadSeoReport = (): Blob => {
  requireMockReport();
  return createMockPdfBlob(mockSeoReportData());
};

export const mockSendSeoReport = (): UberallSeoReportSentPayload => {
  requireMockReport();
  state.lastReportSentAt = iso(0);

  return { sentTo: REPORT_RECIPIENT, sentAt: state.lastReportSentAt };
};

export const mockDatapoints = (query: DatapointsQuery): UberallDataPointsPagePayload => {
  const page = query.page ?? 1;
  const pageSize = query.limit ?? 10;
  const matching = state.datapoints.filter(item => {
    if (query.answered === true && !item.replied) return false;
    if (query.answered === false && item.replied) return false;
    if (query.startDate && item.createdAt && moment(item.createdAt).isBefore(moment(query.startDate))) return false;
    if (query.endDate && item.createdAt && moment(item.createdAt).isAfter(moment(query.endDate))) return false;
    return true;
  });

  return {
    page,
    pageSize,
    count: matching.length,
    unansweredCount: unansweredCount(),
    unreadCount: state.datapoints.filter(item => !item.replied && !item.read).length,
    datapoints: matching.slice((page - 1) * pageSize, page * pageSize),
  };
};

export const mockDatapoint = (datapointId: number): UberallDataPointPayload => {
  const existing = state.datapoints.find(item => item.id === datapointId);
  if (!existing) throw new MockUberallError(404, 'UBERALL_NOT_FOUND', `Unknown datapoint ${datapointId}`);
  return existing;
};

export const mockMarkDatapointRead = (datapointId: number): UberallDataPointPayload => {
  const opened = { ...mockDatapoint(datapointId), read: true };
  state.datapoints = state.datapoints.map(item => (item.id === datapointId ? opened : item));
  return opened;
};

export const mockReplyToDatapoint = (datapointId: number, text: string): UberallDataPointPayload => {
  const existing = state.datapoints.find(item => item.id === datapointId);
  if (!existing) throw new Error(`Unknown datapoint ${datapointId}`);
  const replied: UberallDataPointPayload = { ...existing, replied: true, replyText: text, repliedAt: iso(0) };
  state.datapoints = state.datapoints.map(item => (item.id === datapointId ? replied : item));
  return replied;
};

export const mockAiReply = (datapointId: number, instruction?: string): UberallAiReplyPayload => {
  const target = state.datapoints.find(item => item.id === datapointId);
  const rating = target?.rating ?? 5;
  const author = target?.authorName ?? 'client';
  const signature = state.ai.signature ?? '';
  const body =
    rating <= 2
      ? `Bonjour ${author}, nous sommes sincèrement désolés de cette expérience. Contactez-nous au 03 86 00 00 00 pour que nous trouvions une solution.`
      : `Merci beaucoup ${author} pour ce retour, il compte énormément pour l’équipe. Au plaisir de vous revoir très bientôt.`;

  return {
    datapointId,
    reply: `${body}${instruction ? ` ${instruction}` : ''}${signature ? `\n${signature}` : ''}`,
    requiresManualValidation: rating <= 3,
    provider: 'mock',
    tone: state.ai.tone,
    language: state.ai.language,
    truncated: false,
  };
};

export const mockAiPost = (locationId: number, body: UberallAiPostBody): UberallAiPostPayload => {
  const topic = body.topic?.trim() || 'votre actualité du moment';
  const description = [
    `${topic.charAt(0).toUpperCase()}${topic.slice(1)} : on vous attend au ${state.location.name}.`,
    'Ambiance chaleureuse, carte de saison et une équipe qui connaît ses classiques.',
    body.instruction ? `(${body.instruction})` : '',
    state.ai.signature ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const wantsEmptyTitle = topic.includes('sans-titre');
  const title = wantsEmptyTitle ? '' : topic;
  const draftBody = topic.includes('tres-long') ? description.repeat(40) : description;

  return {
    locationId,
    title: title.slice(0, POST_TITLE_MAX_LENGTH),
    description: draftBody.slice(0, POST_DESCRIPTION_MAX_LENGTH),
    provider: 'mock',
    tone: state.ai.tone,
    language: state.ai.language,
    titleTruncated: title.length > POST_TITLE_MAX_LENGTH,
    descriptionTruncated: draftBody.length > POST_DESCRIPTION_MAX_LENGTH,
  };
};

// --- Social posts ---

const postsPayload = (): UberallSocialPostListPayload => ({
  posts: [...state.posts].sort(
    (a, b) => moment(b.publicationDate ?? 0).valueOf() - moment(a.publicationDate ?? 0).valueOf(),
  ),
  count: state.posts.length,
});

export const mockPosts = () => postsPayload();

export const mockPostDirectories = (): UberallPostDirectoriesPayload => ({
  directories: ['GOOGLE', 'FACEBOOK', 'INSTAGRAM', 'APPLE_MAPS', 'LINKED_IN', 'PAGES24', 'CYLEX', 'PRATIQUE'],
});

export const mockKeywordRankings = (): KeywordRankingsPayload => ({
  periodStart: moment().subtract(3, 'months').startOf('month').format('YYYY-MM-DD'),
  periodEnd: moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
  keywordsTracked: 5,
  rankings: {
    top1Share: 20,
    top3Share: 40,
    top10Share: 60,
    averagePosition: 3.3,
    keywords: [
      { keyword: 'Bar à cocktails Lyon', position: 1, status: 'TOP1' },
      { keyword: 'Happy hour Lyon', position: 2, status: 'TOP3' },
      { keyword: 'Bar terrasse', position: 7, status: 'TOP10' },
      { keyword: 'Bar concert', position: null, status: 'BEYOND_TOP10' },
      { keyword: 'Brunch Lyon', position: null, status: 'UNRANKED' },
    ],
    movers: [{ keyword: 'Bar terrasse', position: 7, previousPosition: 10, delta: 3 }],
  },
});

/** Stands in for the S3 URL the real route answers; the bytes go nowhere. */
export const mockUploadPostVisual = (file: File): string =>
  `https://lb-photos.s3.amazonaws.com/post-visuals/mock-${encodeURIComponent(file.name)}`;

export const mockCreatePost = (body: UberallSocialPostBody): UberallSocialPostListPayload => {
  const created: UberallSocialPostPayload = {
    id: takeId(),
    type: body.type ?? 'POST',
    status: moment(body.publicationDate).isAfter(moment()) ? 'SCHEDULED' : 'PUBLISHED',
    title: body.title,
    description: body.description,
    // Uberall re-hosts what it fetches, so the read never echoes the URL that was sent.
    imageUrl: body.imageUrl ? `https://uberall-userpics-socialpost.example/${takeId()}.jpg` : null,
    url: body.url ?? null,
    publicationDate: body.publicationDate,
    dateStart: body.dateStart ?? null,
    dateEnd: body.dateEnd ?? null,
    directories: body.directories,
    statusPerDirectory: {},
    publicationsCount: body.directories.length,
    isGoogle: body.directories.includes('GOOGLE'),
  };
  state.posts = [...state.posts, created];
  return postsPayload();
};

export const mockUpdatePost = (postId: number, body: UberallSocialPostBody): UberallSocialPostListPayload => {
  state.posts = state.posts.map(post =>
    post.id === postId
      ? {
          ...post,
          type: body.type ?? post.type,
          title: body.title,
          description: body.description,
          /**
           * The three states, mirrored so the mock cannot hide a patch builder that strips nulls:
           * an absent key keeps the current visual, an explicit null clears it, a URL replaces it.
           */
          imageUrl:
            body.imageUrl === undefined
              ? post.imageUrl
              : body.imageUrl === null
                ? null
                : `https://uberall-userpics-socialpost.example/${takeId()}.jpg`,
          url: body.url ?? null,
          publicationDate: body.publicationDate,
          dateStart: body.dateStart ?? null,
          dateEnd: body.dateEnd ?? null,
          directories: body.directories,
          isGoogle: body.directories.includes('GOOGLE'),
        }
      : post,
  );
  return postsPayload();
};

export const mockDeletePost = (postId: number): UberallSocialPostListPayload => {
  state.posts = state.posts.filter(post => post.id !== postId);
  return postsPayload();
};

export const mockSocialConnections = () => state.socialConnections;

// There is no hosted flow to open locally: the popup stays blank and the next poll finds the platform connected.
export const mockSocialConnectUrl = (platform: UberallSocialPlatform) => {
  state.socialConnections =
    platform === 'FACEBOOK'
      ? buildSocialConnections(true)
      : { ...state.socialConnections, instagram: socialConnection('INSTAGRAM', 'CONNECTED', 'planb.auxerre') };
  return { url: 'about:blank' };
};

export const mockDisconnectSocial = (platform: UberallSocialPlatform) => {
  state.socialConnections =
    platform === 'FACEBOOK'
      ? buildSocialConnections(false)
      : { ...state.socialConnections, instagram: socialConnection('INSTAGRAM', 'NOT_CONNECTED') };
  return state.socialConnections;
};
